const HEIDI_API_BASE_URL = 'https://registrar.api.heidihealth.com/api/v2/ml-scribe/open-api';
const HEIDI_API_KEY = process.env.REACT_APP_HEIDI_API_KEY || '';

// Hardcoded values for testing - REMOVE AFTER BACKEND INTEGRATION
const USE_HARDCODED_VALUES = false; // Set to false FIRST TIME to create session, then true
const HARDCODED_SESSION_ID = '162708882398778633578537228994310712392'; // Replace with fresh ID after first run
const HARDCODED_RECORDING_ID = '2d4e8750ebeb4e8b9f28807e903f411d'; // Replace with fresh ID after first run

interface HeidiJWTResponse {
  token: string;
}

interface HeidiSessionResponse {
  session_id: string;
}

interface HeidiTranscriptionInitResponse {
  recording_id: string;
}

interface HeidiTemplateResponse {
  templates: Array<{
    id: string;
    name: string;
  }>;
}

export class HeidiApiService {
  private sessionId: string | null = null;
  private recordingId: string | null = null;
  private jwtToken: string | null = null;
  private audioChunks: Blob[] = [];
  private chunkIndex = 0;

  async getJWT(email: string, thirdPartyInternalId: string): Promise<string> {
    try {
      const response = await fetch(
        `${HEIDI_API_BASE_URL}/jwt?email=${encodeURIComponent(email)}&third_party_internal_id=${encodeURIComponent(
          thirdPartyInternalId
        )}`,
        {
          method: 'GET',
          headers: {
            'Heidi-Api-Key': HEIDI_API_KEY,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get JWT: ${response.statusText}`);
      }

      const data: HeidiJWTResponse = await response.json();
      this.jwtToken = data.token;
      return data.token;
    } catch (error) {
      console.error('Error getting JWT:', error);
      throw error;
    }
  }

  async createSession(): Promise<string> {
    // Use hardcoded session ID for testing
    if (USE_HARDCODED_VALUES) {
      console.log('🔧 Using hardcoded session ID for testing:', HARDCODED_SESSION_ID);
      this.sessionId = HARDCODED_SESSION_ID;
      return HARDCODED_SESSION_ID;
    }

    if (!this.jwtToken) {
      throw new Error('JWT token not available. Call getJWT first.');
    }

    try {
      const response = await fetch(`${HEIDI_API_BASE_URL}/sessions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.jwtToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to create session: ${response.statusText}`);
      }

      const data: HeidiSessionResponse = await response.json();
      this.sessionId = data.session_id;
      return data.session_id;
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

  async updateSession(patientInfo?: any, language?: string): Promise<void> {
    if (!this.sessionId || !this.jwtToken) {
      throw new Error('Session or JWT not available.');
    }

    try {
      const response = await fetch(`${HEIDI_API_BASE_URL}/sessions/${this.sessionId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${this.jwtToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(patientInfo && { patient_info: patientInfo }),
          ...(language && { language }),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update session: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error updating session:', error);
      throw error;
    }
  }

  async initializeTranscription(): Promise<string> {
    // Use hardcoded recording ID for testing
    if (USE_HARDCODED_VALUES) {
      console.log('🔧 Using hardcoded recording ID for testing:', HARDCODED_RECORDING_ID);
      this.recordingId = HARDCODED_RECORDING_ID;
      return HARDCODED_RECORDING_ID;
    }

    if (!this.sessionId || !this.jwtToken) {
      throw new Error('Session or JWT not available.');
    }

    try {
      const response = await fetch(`${HEIDI_API_BASE_URL}/sessions/${this.sessionId}/restful-segment-transcription`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.jwtToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to initialize transcription: ${response.statusText}`);
      }

      const data: HeidiTranscriptionInitResponse = await response.json();
      this.recordingId = data.recording_id;
      return data.recording_id;
    } catch (error) {
      console.error('Error initializing transcription:', error);
      throw error;
    }
  }

  async uploadAudioChunk(audioBlob: Blob): Promise<void> {
    if (!this.sessionId || !this.recordingId || !this.jwtToken) {
      throw new Error('Session, recording ID, or JWT not available.');
    }

    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.wav'); // Changed to .wav
      formData.append('index', this.chunkIndex.toString());

      console.log('📤 Uploading chunk:', {
        size: audioBlob.size,
        index: this.chunkIndex,
        type: audioBlob.type,
        filename: 'audio.wav',
      });

      const response = await fetch(
        `${HEIDI_API_BASE_URL}/sessions/${this.sessionId}/restful-segment-transcription/${this.recordingId}:transcribe`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.jwtToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to upload audio chunk: ${response.statusText}`);
      }

      this.chunkIndex++;
    } catch (error) {
      console.error('Error uploading audio chunk:', error);
      throw error;
    }
  }

  async finishTranscription(): Promise<void> {
    if (!this.sessionId || !this.recordingId || !this.jwtToken) {
      throw new Error('Session, recording ID, or JWT not available.');
    }

    try {
      const response = await fetch(
        `${HEIDI_API_BASE_URL}/sessions/${this.sessionId}/restful-segment-transcription/${this.recordingId}:finish`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.jwtToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to finish transcription: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error finishing transcription:', error);
      throw error;
    }
  }

  async getTranscript(): Promise<any> {
    if (!this.sessionId || !this.jwtToken) {
      throw new Error('Session or JWT not available.');
    }

    try {
      const response = await fetch(`${HEIDI_API_BASE_URL}/sessions/${this.sessionId}/transcript`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.jwtToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get transcript: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting transcript:', error);
      throw error;
    }
  }

  async getTemplates(): Promise<any> {
    if (!this.jwtToken) {
      throw new Error('JWT not available.');
    }

    try {
      const response = await fetch(`${HEIDI_API_BASE_URL}/templates/document-templates`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.jwtToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get templates: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting templates:', error);
      throw error;
    }
  }

  async generateConsultNote(
    templateId: string,
    generationMethod = 'TEMPLATE',
    voiceStyle = 'GOLDILOCKS',
    brain = 'LEFT'
  ): Promise<void> {
    if (!this.sessionId || !this.jwtToken) {
      throw new Error('Session or JWT not available.');
    }

    try {
      // This API returns SSE (Server-Sent Events) streaming data
      // We just trigger it and don't wait for the stream
      const response = await fetch(`${HEIDI_API_BASE_URL}/sessions/${this.sessionId}/consult-note`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.jwtToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          generation_method: generationMethod,
          template_id: templateId,
          voice_style: voiceStyle,
          brain: brain,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate consult note: ${response.statusText}`);
      }

      // Don't parse - this is a streaming response
      console.log('📝 Consult note generation started (streaming)');
    } catch (error) {
      console.error('Error generating consult note:', error);
      throw error;
    }
  }

  async getCustomNotes(template: any, questions: any[], summaryRequired: boolean, metadata?: any): Promise<any> {
    if (!this.sessionId || !this.jwtToken) {
      throw new Error('Session or JWT not available.');
    }

    try {
      const response = await fetch(
        `${HEIDI_API_BASE_URL}/sessions/${this.sessionId}/client-customised-template/response`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.jwtToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            template,
            questions,
            summaryRequired,
            ...(metadata && { metadata }),
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get custom notes: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting custom notes:', error);
      throw error;
    }
  }

  async getLiveTranscript(): Promise<any> {
    if (!this.sessionId) {
      throw new Error('Session not available.');
    }

    try {
      const response = await fetch(
        `${HEIDI_API_BASE_URL}/sessions/${this.sessionId}/transcript/live?recording_status=in_progress`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${this.jwtToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get live transcript: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting live transcript:', error);
      throw error;
    }
  }

  async getSessionStatus(): Promise<any> {
    if (!this.sessionId || !this.jwtToken) {
      throw new Error('Session or JWT not available.');
    }

    try {
      const response = await fetch(`${HEIDI_API_BASE_URL}/sessions/${this.sessionId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.jwtToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get session status: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting session status:', error);
      throw error;
    }
  }

  resetSession(): void {
    this.sessionId = null;
    this.recordingId = null;
    this.jwtToken = null;
    this.audioChunks = [];
    this.chunkIndex = 0;
  }

  getSessionId(): string | null {
    return this.sessionId;
  }
}

export const heidiApiService = new HeidiApiService();
